import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  usePatchTargetsById,
  usePostTargetsTestConnection,
  useGetAgents,
  getGetProfilesByIdQueryKey,
} from '@/api/control/control-plane/control-plane';
import type {
  TestAzureSqlConnectionRequest,
  ProfileDto,
  TargetDto,
  UpdateTargetRequest,
} from '@/api/control/missionImpactAPI.schemas';
import { useRoles } from '@/contexts/RolesContext';
import { Button } from '@/components/ui/button';
import { LoadingButton } from '@/components/LoadingButton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Pencil, AlertCircle } from 'lucide-react';
import { ConnectionTestAlert } from '@/components/common/ConnectionTestAlert';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { useError } from '@/contexts/ErrorContext';
import { FirewallHelpDialog } from '@/components/common/FirewallHelpDialog';

/**
 * Props for the EditTargetDialog component
 */
interface EditTargetDialogProps {
  /** The organization ID */
  orgId: string;
  /** The profile ID */
  profileId: string;
  /** The target to edit */
  target: TargetDto;
  /** The region ID for the profile, used to display NAT IP in firewall help */
  regionId?: string | null;
  /** Optional callback to execute after successful update */
  onSuccess?: () => void;
  /** Optional external control of dialog open state */
  externalOpen?: boolean;
  /** Optional callback when external open state should change */
  onExternalOpenChange?: (open: boolean) => void;
}

/**
 * Dialog for editing target authentication credentials and agent assignment.
 * Auth type is locked to whatever the target was created with.
 */
export function EditTargetDialog({ orgId, profileId, target, regionId, onSuccess, externalOpen, onExternalOpenChange }: EditTargetDialogProps) {
  const { isAdminOrOperator } = useRoles();
  const { t } = useLanguage();
  const { handleApiError } = useError();
  const queryClient = useQueryClient();
  const [internalOpen, setInternalOpen] = useState(false);

  // Use external open state if provided, otherwise use internal state
  const isOpen = externalOpen ?? internalOpen;
  const setIsOpen = onExternalOpenChange ?? setInternalOpen;

  // Auth type is fixed from target creation
  const isAzureSql = target.$type === 'AzureSql';

  // Service principal credentials
  const [tenantId, setTenantId] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');

  // SQL credential credentials
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [encryptConnection, setEncryptConnection] = useState(true);

  // Agent
  const [selectedAgentId, setSelectedAgentId] = useState<string>('');

  // Error state
  const [error, setError] = useState<string | null>(null);

  // Connection test state
  const [connectionTestResult, setConnectionTestResult] = useState<null | 'success' | 'error'>(null);
  const [connectionTestError, setConnectionTestError] = useState<string | null>(null);
  const [firewallDialogOpen, setFirewallDialogOpen] = useState(false);

  // Fetch agents for agent selector
  const { data: agentsResponse } = useGetAgents(orgId);
  const agents = agentsResponse?.data ?? [];

  // Test connection mutation
  const { mutate: testConnection, isPending: isTesting } = usePostTargetsTestConnection({
    mutation: {
      onSuccess: () => {
        setConnectionTestResult('success');
        setConnectionTestError(null);
      },
      onError: (err) => {
        setConnectionTestResult('error');
        const { message, errorCode } = handleApiError(
          err,
          { context: 'EditTargetDialog.testConnection', profile: { regionId } as ProfileDto },
          { showToast: false },
        );

        if (errorCode === 'AzureSqlFirewallBlocked') {
          setFirewallDialogOpen(true);
        }

        setConnectionTestError(message);
      },
    },
  });

  /**
   * Handles testing the connection with the current form values
   */
  const handleTestConnection = () => {
    setError(null);
    setConnectionTestResult(null);
    setConnectionTestError(null);

    const agentIdValue = selectedAgentId && selectedAgentId !== 'none' ? selectedAgentId : undefined;

    let payload: TestAzureSqlConnectionRequest;
    if (isAzureSql) {
      if (!tenantId.trim() || !clientId.trim() || !clientSecret.trim()) {
        return;
      }
      payload = {
        $type: 'AadApp',
        tenantId: tenantId.trim(),
        clientId: clientId.trim(),
        clientSecret: clientSecret.trim(),
        server: target.server,
        database: target.database,
        schemaName: target.schemaName,
        agentId: agentIdValue,
      };
    } else {
      if (!username.trim() || !password.trim()) {
        return;
      }
      payload = {
        $type: 'Basic',
        username: username.trim(),
        password: password.trim(),
        encryptConnection,
        server: target.server,
        database: target.database,
        schemaName: target.schemaName,
        agentId: agentIdValue,
      };
    }

    testConnection({
      orgId,
      profileId,
      data: payload,
    });
  };

  /**
   * Resets credential fields to the target's current values
   */
  const resetCredentials = () => {
    if (target.$type === 'AzureSql') {
      setTenantId(target.tenantId);
      setClientId(target.clientId);
      setClientSecret('');
    } else {
      setUsername(target.username);
      setPassword('');
      setEncryptConnection(target.encryptConnection ?? true);
    }
    setConnectionTestResult(null);
    setConnectionTestError(null);
  };

  // Update form when target changes
  useEffect(() => {
    resetCredentials();
    setSelectedAgentId(target.agentId ?? '');
  }, [target]);

  // Update mutation
  const { mutate: updateTarget, isPending } = usePatchTargetsById({
    mutation: {
      onSuccess: () => {
        toast.success(t.targets.editDialog.updateSuccess);
        setError(null);
        setClientSecret('');
        setPassword('');
        invalidateQueries();
        onSuccess?.();
      },
      onError: (err: unknown) => {
        const { message } = handleApiError(err, { context: 'EditTargetDialog.update' }, { showToast: false });
        setError(message);
      },
    },
  });

  /**
   * Invalidates queries to refetch profile with updated target
   */
  const invalidateQueries = () => {
    void queryClient.invalidateQueries({
      queryKey: ['targets', 'by-profile', profileId],
    });
    void queryClient.invalidateQueries({
      queryKey: getGetProfilesByIdQueryKey(orgId, profileId),
    });
  };

  /**
   * Handles the save action - sends credentials config + agentId in a single PATCH
   */
  const handleSave = () => {
    setError(null);

    let config: UpdateTargetRequest['config'];
    if (isAzureSql) {
      if (!tenantId.trim()) {
        setError(t.targets.editDialog.tenantIdRequired);
        return;
      }
      if (!clientId.trim()) {
        setError(t.targets.editDialog.clientIdRequired);
        return;
      }
      if (!clientSecret.trim()) {
        setError(t.targets.editDialog.clientSecretRequired);
        return;
      }
      config = {
        $type: 'AadApp',
        tenantId,
        clientId,
        clientSecret,
      };
    } else {
      if (!username.trim()) {
        setError(t.targets.editDialog.usernameRequired);
        return;
      }
      if (!password.trim()) {
        setError(t.targets.editDialog.passwordRequired);
        return;
      }
      config = {
        $type: 'Basic',
        username,
        password,
        encryptConnection,
      };
    }

    const agentIdValue = selectedAgentId && selectedAgentId !== 'none' ? selectedAgentId : null;
    const data: UpdateTargetRequest = { config, agentId: agentIdValue };
    updateTarget({
      orgId,
      profileId,
      targetId: target.id,
      data,
    });
  };

  /**
   * Handles dialog close and resets form
   */
  const handleClose = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      resetCredentials();
      setSelectedAgentId(target.agentId ?? '');
      setError(null);
      setConnectionTestResult(null);
      setConnectionTestError(null);
    }
  };

  // Whether all required credential fields are filled for the target's auth type
  const areCredentialsFilled = isAzureSql
    ? tenantId.trim() && clientId.trim() && clientSecret.trim()
    : username.trim() && password.trim();

  // Hide built-in trigger when in controlled mode (externalOpen is provided)
  const isControlled = externalOpen !== undefined;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      {isAdminOrOperator && !isControlled && (
        <DialogTrigger asChild>
          <Button variant="outline">
            <Pencil className="mr-2 h-4 w-4" />
            {t.targets.editDialog.buttonText}
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{t.targets.editDialog.title}</DialogTitle>
          <DialogDescription>{t.targets.editDialog.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Agent selector */}
          {agents.length > 0 && (
            <div className="grid gap-2">
              <Label>{t.targets.editDialog.agent}</Label>
              <Select value={selectedAgentId || 'none'} onValueChange={(value) => { setSelectedAgentId(value); setConnectionTestResult(null); }} disabled={isTesting}>
                <SelectTrigger>
                  <SelectValue placeholder={t.targets.editDialog.agentPlaceholder} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t.targets.editDialog.agentNone}</SelectItem>
                  {agents.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {t.targets.editDialog.agentDescription}
              </p>
            </div>
          )}

          {/* Credential fields - based on the target's fixed auth type */}
          {isAzureSql ? (
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="editTenantId">{t.targets.editDialog.tenantId} *</Label>
                <Input
                  id="editTenantId"
                  type="text"
                  value={tenantId}
                  onChange={(e) => { setTenantId(e.target.value); setConnectionTestResult(null); }}
                  placeholder={t.targets.editDialog.tenantIdPlaceholder}
                  className="font-mono text-sm"
                  disabled={isTesting}
                />
                <p className="text-xs text-muted-foreground">
                  {t.targets.editDialog.tenantIdDescription}
                </p>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="editClientId">{t.targets.editDialog.clientId} *</Label>
                <Input
                  id="editClientId"
                  type="text"
                  value={clientId}
                  onChange={(e) => { setClientId(e.target.value); setConnectionTestResult(null); }}
                  placeholder={t.targets.editDialog.clientIdPlaceholder}
                  className="font-mono text-sm"
                  disabled={isTesting}
                />
                <p className="text-xs text-muted-foreground">
                  {t.targets.editDialog.clientIdDescription}
                </p>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="editClientSecret">{t.targets.editDialog.clientSecret} *</Label>
                <Input
                  id="editClientSecret"
                  type="password"
                  value={clientSecret}
                  onChange={(e) => { setClientSecret(e.target.value); setConnectionTestResult(null); }}
                  placeholder={t.targets.editDialog.clientSecretPlaceholder}
                  className="font-mono text-sm"
                  disabled={isTesting}
                />
                <p className="text-xs text-muted-foreground">
                  {t.targets.editDialog.clientSecretDescription}
                </p>
              </div>
            </div>
          ) : (
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="editUsername">{t.targets.editDialog.username} *</Label>
                <Input
                  id="editUsername"
                  type="text"
                  value={username}
                  onChange={(e) => { setUsername(e.target.value); setConnectionTestResult(null); }}
                  placeholder={t.targets.editDialog.usernamePlaceholder}
                  className="font-mono text-sm"
                  disabled={isTesting}
                />
                <p className="text-xs text-muted-foreground">
                  {t.targets.editDialog.usernameDescription}
                </p>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="editPassword">{t.targets.editDialog.password} *</Label>
                <Input
                  id="editPassword"
                  type="password"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setConnectionTestResult(null); }}
                  placeholder={t.targets.editDialog.passwordPlaceholder}
                  className="font-mono text-sm"
                  disabled={isTesting}
                />
                <p className="text-xs text-muted-foreground">
                  {t.targets.editDialog.passwordDescription}
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="editEncryptConnection"
                  checked={encryptConnection}
                  onCheckedChange={(checked) => { setEncryptConnection(checked === true); setConnectionTestResult(null); }}
                  disabled={isTesting}
                />
                <label
                  htmlFor="editEncryptConnection"
                  className="text-sm cursor-pointer"
                >
                  {t.targets.editDialog.encryptConnection}
                </label>
              </div>
              <p className="text-xs text-muted-foreground">
                {t.targets.editDialog.encryptConnectionDescription}
              </p>
            </div>
          )}

          {/* Test Connection Button */}
          <LoadingButton
            type="button"
            variant="outline"
            onClick={handleTestConnection}
            isLoading={isTesting}
            disabled={!areCredentialsFilled}
            className="w-full"
          >
            {t.targets.editDialog.testConnection}
          </LoadingButton>

          {/* Connection Test Result */}
          {connectionTestResult === 'success' && (
            <ConnectionTestAlert
              success={true}
              message={t.targets.editDialog.connectionSuccessful}
            />
          )}
          {connectionTestResult === 'error' && connectionTestError && (
            <ConnectionTestAlert
              success={false}
              message={connectionTestError}
            />
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { handleClose(false); }}>
            {t.targets.editDialog.cancel}
          </Button>
          <LoadingButton
            onClick={handleSave}
            disabled={!areCredentialsFilled || connectionTestResult !== 'success'}
            isLoading={isPending}
          >
            {t.targets.editDialog.update}
          </LoadingButton>
        </DialogFooter>
      </DialogContent>

      {/* Firewall Configuration Dialog */}
      <FirewallHelpDialog
        open={firewallDialogOpen}
        onOpenChange={setFirewallDialogOpen}
        regionId={regionId}
      />
    </Dialog>
  );
}
